import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Empty,
  Pagination,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  message,
  Modal,
  notification,
} from "antd";
import {
  BellTwoTone,
  CheckCircleOutlined,
  CheckOutlined,
  ExclamationCircleOutlined,
  ToolOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import get from "lodash/get";
import map from "lodash/map";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  getNotificationList,
  markNotificationRead,
  markAllNotificationRead,
  getNotificationMetaConfig,
} from "../../redux/actions/notificationActions";
import NotificationCard from "../../components/notification/notificationCard";
import { set, template } from "lodash";

const { Text, Title } = Typography;
const { confirm } = Modal;

const intialFilterOptions = {
  template_id: undefined,
  notification_type: undefined,
  is_readed: undefined,
};

export default function NotificationPage() {
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const params = new URLSearchParams(get(location, "search", ""));

  const [filterOptions, setFilterOptions] = useState(
    params.get("template")
      ? {
          ...intialFilterOptions,
          template_id: params.get("template"),
        }
      : intialFilterOptions,
  );

  const [config, setConfig] = useState(undefined);
  const dispatch = useDispatch();

  const [notifications, setNotifications] = useState({
    data: [],
    pagination: {
      current_page: 1,
    },
  });

  const fetchNotifications = (page = 1, filters = {}) => {
    setLoading(true);

    dispatch(getNotificationList({ page, ...filters }))
      .then((result) => {
        setNotifications({
          data: get(result, "data"),
          pagination: get(result, "meta.pagination"),
        });
        setFilterOptions(filters);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const acknowledgeNotification = (id) => {
    setLoading(true);
    dispatch(markNotificationRead(id))
      .then(() => {
        fetchNotifications(get(notifications, "pagination.current_page", 1));
        message.success("Notification acknowledged");
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const acknowledgeAllNotification = () => {
    setLoading(true);

    dispatch(markAllNotificationRead())
      .then(() => {
        message.success("All notifications acknowledged");
        fetchNotifications(get(notifications, "pagination.current_page", 1));
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const fetchMetaConfig = () => {
    setLoading(true);
    dispatch(getNotificationMetaConfig())
      .then((result) => {
        setConfig(get(result, "config"));
      })
      .catch(() => {});
  };

  useEffect(() => {
    fetchNotifications(1, filterOptions);
    fetchMetaConfig();
  }, []);

  useEffect(() => {
    const interval = setInterval(
      () => {
        const currentPage = get(notifications, "pagination.current_page", 1);

        if (currentPage === 1) {
          fetchNotifications(1, filterOptions);
        }
      },
      2 * 60 * 1000,
    );

    return () => clearInterval(interval);
  }, [notifications, filterOptions]);

  const onApplyFilter = () => {};

  const templateOptions = Object.entries(get(config, "templates", {})).map(
    ([company, templates]) => ({
      label: company,
      options: templates.map((template) => ({
        label: template.name,
        value: template.id,
        searchText: `${company} ${template.name}`,
      })),
    }),
  );

  return (
    <Spin spinning={loading}>
      <div className="p-4 bg-slate-50 min-h-screen">
        {/* HEADER */}

        <div className="flex justify-between items-center mb-4">
          <div>
            <Title level={3} className="!mb-0">
              Notifications
            </Title>

            <Text type="secondary">Monitor machine alerts</Text>
          </div>

          <Space>
            <Button
              danger
              size="small"
              type="primary"
              onClick={() => {
                confirm({
                  maskClosable: true,
                  title: "Acknowledge All Notifications",
                  content:
                    "Are you sure you want to acknowledge all notifications?",
                  okText: "Yes",
                  cancelText: "No",
                  okType: "primary",
                  onOk() {
                    acknowledgeAllNotification();
                  },
                });
              }}
            >
              Acknowledge All
            </Button>
          </Space>
        </div>

        <Card size="small" className="sticky top-0 z-10 mb-4 shadow-sm">
          <div className="flex flex-wrap gap-3">
            <Select
              size="small"
              placeholder="Template"
              showSearch
              style={{ width: 360 }}
              value={get(filterOptions, "template_id")}
              onChange={(val) => {
                fetchNotifications(1, {
                  ...filterOptions,
                  template_id: val,
                });
              }}
              options={templateOptions}
              filterOption={(input, option) =>
                option?.searchText?.toLowerCase().includes(input.toLowerCase())
              }
            />

            <Select
              size="small"
              placeholder="Type"
              style={{ width: 270 }}
              value={get(filterOptions, "notification_type")}
              onChange={(val) => {
                fetchNotifications(1, {
                  ...filterOptions,
                  notification_type: val,
                });
              }}
              fieldNames={{
                value: "id",
                label: "name",
              }}
              options={get(config, "notification_types", [])}
            />

            <Select
              size="small"
              placeholder="Status"
              style={{ width: 270 }}
              value={get(filterOptions, "is_readed")}
              onChange={(val) => {
                fetchNotifications(1, {
                  ...filterOptions,
                  is_readed: val,
                });
              }}
              options={[
                {
                  label: "Pending",
                  value: false,
                },
                {
                  label: "Acknowledged",
                  value: true,
                },
              ]}
            />

            <Button
              size="small"
              style={{ width: 90 }}
              onClick={() => {
                fetchNotifications(1, intialFilterOptions);
              }}
            >
              Reset
            </Button>
          </div>
        </Card>
        <div className="space-y-2">
          {get(notifications, "data", []).length === 0 ? (
            <Card size="small">
              <Empty />
            </Card>
          ) : (
            map(get(notifications, "data", []), (notification) => {
              return (
                <NotificationCard
                  notification={notification}
                  acknowledgeNotification={acknowledgeNotification}
                />
              );
            })
          )}
        </div>

        {/* PAGINATION */}

        <div className="flex justify-end mt-4">
          <Pagination
            current={get(notifications, "pagination.current_page", 0)}
            pageSize={get(notifications, "pagination.per_page", 0)}
            total={get(notifications, "pagination.total", 0)}
            onChange={get(notifications, "pagination.total_pages", 0)}
            showQuickJumper={false}
            showSizeChanger={false}
            onChange={(page) => {
              fetchNotifications(page, filterOptions);
            }}
          />
        </div>
      </div>
    </Spin>
  );
}
